import {
  doesQualifyToBeHidden,
  ItemAvailabilityIds,
} from './ItemAvailabilityStatus'
import type { TMSService } from '~/types/msService/models'
import type { IStateItem } from '~/types/models'
import { isItemProduct } from '~/types/product/models'

export function getCountableMSServicesCount(
  msServices: TMSService[] | undefined | null
) {
  return getCountableMSServices(msServices).length
}

export function getCountableMSServices(
  msServices: TMSService[] | undefined | null
) {
  return msServices?.filter(isCountableMSService) ?? []
}

/**
 * The least an msService has to look like for `isSupersededMSService`.
 * Structural rather than `TMSService` so the menu predicates can reuse it:
 * those take a `Pick`ed slice of the entity, not the whole thing.
 */
export interface IMSServiceWithAvailability {
  itemAvailabilityState?: IStateItem
}

/**
 * Kept for the record, no longer part of the configuration — the user swapped
 * it for another alternative from the same Set, or took it out outright. This is
 * what nearly all consuming code wants: counts, money roll-ups and status
 * ladders care that the row is out, not about how it went. Which of the two it
 * was is carried by the state's own `descr` and read straight off the
 * Inclusion column; no predicate needs to tell them apart.
 *
 * 🔑 THE MARKER IS AN AVAILABILITY STATE AND NOT A STATUS, deliberately, and
 * that is worth keeping in view when reading the callers. It was tried as two
 * status ids first and the model does not survive contact with the kit
 * cascades: `setMSServiceStatus` alone is reached from 12 sites in
 * SiloOverview.ts, each passing `doIncludeDoesQualifyToBeHidden: true`, so any
 * kit-level status action wrote straight over the marker and brought the row
 * back — visible, countable and contributing money. Nothing cascades
 * itemAvailabilityState. It also lets the server agree with us through the
 * generated `qualifies_to_be_hidden` column rather than a parallel status
 * clause, and needs one shared enum instead of an id kept free in three
 * unrelated silo status spaces.
 *
 * ⚠ A superseded row therefore still carries its real lifecycle status, and
 * that status can be anything — including one a live row would have. Never
 * infer "superseded" from a status, and never treat a plausible status as
 * evidence a row is live.
 */
export function isSupersededMSService(
  msService: IMSServiceWithAvailability | undefined | null
) {
  const availabilityId = msService?.itemAvailabilityState?.id
  return (
    availabilityId === ItemAvailabilityIds.OPTIONAL_SWAPPED_OUT ||
    availabilityId === ItemAvailabilityIds.OPTIONAL_REMOVED
  )
}

/**
 * The superseded clause is now BELT-AND-BRACES rather than load-bearing, and it
 * is kept for what it says rather than what it does. `doesQualifyToBeHidden`
 * covers superseded rows by construction — both markers are availability states
 * and it tests that one field — so the clause below can no longer be the only
 * thing keeping a swapped-away row out of `numServices`, the master service's
 * status-count chips, or a contract's service count. It was exactly that while
 * the marker was a status.
 *
 * ⚠ Do not take this as licence to drop the same clause from
 * `getMSServicesToRollUp` in {Opportunity,Order,Company}MSService.ts. Those
 * never call `doesQualifyToBeHidden`, so there the test is the only exclusion
 * there is.
 *
 * The server says the same thing in its `countable_<table>` views, and — since
 * the marker moved onto `itemavailabilitystate_sn` — says it through the same
 * generated `qualifies_to_be_hidden` column this function reaches through,
 * rather than a parallel status clause that had to be kept in step by hand.
 */
export function isCountableMSService(msService: TMSService | undefined | null) {
  return (
    msService?.doInclude &&
    !doesQualifyToBeHidden(msService) &&
    !isSupersededMSService(msService) &&
    isItemProduct(msService.product)
  )
}
